import { useAuth } from '@/hooks/useAuth';
import { useUserProfile } from '@/hooks/useUserProfile';
import { SidebarProvider, SidebarTrigger, useSidebar } from '@/components/ui/sidebar';
import { AppSidebar } from './AppSidebar';
import { Button } from '@/components/ui/button';
import { LogOut } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { APP_VERSION } from '@/lib/version';
import VersionInfo from './VersionInfo';
import { ZelofinLogo } from './ZelofinLogo';

interface LayoutProps {
  children: React.ReactNode;
}

function LayoutContent({ children }: LayoutProps) {
  const { user, signOut } = useAuth();
  const { profile } = useUserProfile();
  const { open } = useSidebar();

  const handleSignOut = async () => {
    await signOut();
  };

  // Prefer profile name > auth metadata > email prefix
  const displayName =
    profile.full_name ||
    user?.user_metadata?.full_name ||
    user?.email?.split('@')[0] ||
    'Usuário';

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="min-h-screen flex w-full bg-background">
      <AppSidebar />
      <div className="flex-1 flex flex-col">
        <header className="sticky top-0 z-20 border-b border-border/80 bg-background/80 backdrop-blur-xl">
          <div className="flex h-14 sm:h-20 items-center justify-between px-4 sm:px-6 lg:px-8 xl:px-10">
            <div className="flex items-center gap-4">
            <SidebarTrigger className="lg:hidden" />
            <div className="hidden lg:block">
              <SidebarTrigger />
            </div>
            <div
              className={`flex items-center transition-opacity duration-300 ease-in-out ${open ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
            >
              <ZelofinLogo variant="icon-text" className="h-8 w-auto sm:h-9" />
            </div>
            </div>
            
            <div className="flex items-center gap-3 rounded-[20px] border border-border/80 bg-card/80 px-3 py-2 shadow-xs sm:px-4">
              <Avatar className="h-10 w-10 border border-border/70">
                <AvatarFallback className="bg-gradient-primary text-primary-foreground text-sm font-semibold">
                  {getInitials(displayName)}
                </AvatarFallback>
              </Avatar>
              <div className="hidden sm:block text-left">
                <p className="text-sm font-semibold">{displayName}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSignOut}
                className="border-border/80"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline ml-2">Sair</span>
              </Button>
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 py-5 sm:px-6 lg:px-8 xl:px-10">
          {children}
        </main>

        <footer className="border-t border-border/70 bg-background/70 px-4 py-4 sm:px-6 lg:px-8 xl:px-10">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <span className="font-medium text-foreground">Zelofin</span>
              <VersionInfo />
            </div>
            <span>v{APP_VERSION}</span>
          </div>
        </footer>
        </div>
      </div>
  );
}

export function Layout({ children }: LayoutProps) {
  return (
    <SidebarProvider defaultOpen={false}>
      <LayoutContent>{children}</LayoutContent>
    </SidebarProvider>
  );
}